/**
 *
 * Lures/ChannelSelector/SpecialLureCard
 *
 */

import React, { useState, useEffect, useRef, useMemo, useCallback, createContext, useContext } from 'react'

// NPM Libraries
import styled from '@emotion/styled'
import { motion, useAnimation } from 'framer-motion'
import USPSDeliverIcon from './DriverJoey.png'

const Container = styled.div`
	position: absolute;
	top: 0;
	left: 0;
	min-height: 90vh;
	width: 100%;
	display: flex;
	align-items: center;
	justify-content: center;
	overflow: hidden;
	pointer-events: none;
`

const SvgRunnerCtx = createContext({ run: () => {}, running: false })

export function useCoolSvg() {
	return useContext(SvgRunnerCtx)
}

export function CoolSvgProvider({ children }) {
	const controls = useAnimation()
	const [running, setRunning] = useState(false)
	const mountedRef = useRef(false)

	useEffect(() => {
		mountedRef.current = true
		return () => {
			mountedRef.current = false
		}
	}, [])

	const traverse = useCallback(
		async (toX, scale, mirror = false, duration = 2.2) => {
			if (!mountedRef.current) return
			const scaleX = mirror ? -Math.abs(scale) : Math.abs(scale)

			await controls.start({ x: toX, scaleX, scaleY: Math.abs(scale), transition: { duration, ease: 'linear' } })

			await controls.set({ scaleX: 1, scaleY: 1 })
		},
		[controls],
	)

	const run = useCallback(async () => {
		if (running || !mountedRef.current) return
		setRunning(true)

		// Start off-screen RIGHT for first pass (R -> L)
		await controls.set({ x: '130vw', opacity: 1, scaleX: 1, scaleY: 1 })

		// 1) Right -> Left, scale 1, normal
		await traverse('-20vw', 1, false, 3.5)

		// 2) Left -> Right, scale 2.5, mirrored
		await controls.set({ x: '130cw', scaleX: -3, scaleY: 3 }) // jump to left mirrored
		await traverse('120vw', 3, true, 2)

		// 3) Right -> Left, scale 0.5, normal
		await controls.set({ x: '120vw', scale: 0.8 }) // jump to left mirrored
		await traverse('-20vw', 0.8, false, 1)

		// 4) Left -> Right, scale 1.5, mirrored
		await controls.set({ x: '-20vw', scaleX: -2, scaleY: 2 }) // jump to left mirrored
		await traverse('120vw', 2, true, 2)

		// 5) Left -> Right (stop at center), scale 4, mirrored
		await controls.set({ x: '130vw', scale: 5 }) // jump to left mirrored
		await controls.start({ x: '40vw', transition: { duration: 2, ease: 'linear' } })
		await new Promise((r) => setTimeout(r, 1000)) // pause 1s at center
		await controls.start({ x: '-100vw', transition: { duration: 1, ease: 'linear' } })

		await controls.set({ scale: 1 }) // Reset

		if (mountedRef.current) setRunning(false)
	}, [controls, running, traverse])

	const value = useMemo(() => ({ run, running, _controls: controls }), [run, running, controls])

	return <SvgRunnerCtx.Provider value={value}>{children}</SvgRunnerCtx.Provider>
}

export function LetsGoJoe() {
	const { _controls } = useCoolSvg()

	return (
		<Container>
			<div className="w-100">
				<motion.div
					initial={{ x: '-20vw', opacity: 0 }}
					animate={_controls}
					style={{
						position: 'absolute',
						top: '50%',
						transform: 'translateY(-50%)',
						width: 200,
						zIndex: 1000000,
					}}
				>
					<img src={USPSDeliverIcon} style={{ height: '10rem' }} alt="" />
				</motion.div>
			</div>
		</Container>
	)
}

export default LetsGoJoe
